import "./users.less";
import template from "./users.html";
import {
	FORM_USERS,
	PASSWORD_RESET,
	INVITE_RESEND
} from "routes.js";
import {
	FuzzySearcher,
	FUZZY_OPTIONS
} from "../../utils/fuzzy-search";
import * as filters from "../../filters/filters.js";
import { ROLE_TYPES } from "../../config/permissions";
import moment from "moment";

function controller(
	$userProfile,
	$uibModal,
	$logger,
	tableHelper,
	appState,
	api,
	loadingService,
	toastr,
	promiseTracker,
	$stateParams,
	$location,
	$scope
) {
	const ctrl = this;

	ctrl.promiseTracker = loadingService.getTracker();

	ctrl.$onInit = onInit;
	ctrl._uibModal = $uibModal;

	ctrl.resetPassword = resetPassword;
	ctrl.resendInvite = resendInvite;
	ctrl.onCollapse = onCollapse;
	ctrl.onLastLoginChange = onLastLoginChange;

	ctrl.tableWidthCss = "col-sm-9";
	ctrl.windowResizeDesktopCallback = windowResizeDesktopCallback;
	ctrl.impersonate = impersonate;
	ctrl.signinTracker = promiseTracker();

	ctrl.searcher = new FuzzySearcher([], FUZZY_OPTIONS.USERS);

	ctrl.loginRanges = [
		{ key: 0, display: "All", rangeBegin: null },
		{ key: 1, display: "Never", rangeBegin: moment() },
		{ key: 2, display: "Last Day", rangeBegin: moment().subtract(1, "day") },
		{ key: 3, display: "Last Week", rangeBegin: moment().subtract(1, "week") },
		{ key: 4, display: "Last Month", rangeBegin: moment().subtract(1, "month") },
		{ key: 6, display: "Last 6 Months", rangeBegin: moment().subtract(6, "month") },
		{ key: 5, display: "Last Year", rangeBegin: moment().subtract(1, "year") }
	];
	ctrl.lastLoginRange = ctrl.loginRanges[0];

	ctrl.table = {
		data: [],
		allData: [],
		actions: {},
		sort: {
			predicate: "meta.username", // custom property added since angular-data-grid can't handle multiple sort functionality
			direction: "asc"
		},
		filter: () => {
			tableHelper.filter(ctrl.table, ctrl.searcher, { 
				defaultSortBy: "Contact.FirstName",
				logger: $logger
			}); 
			ctrl.paginatorOptions = {
				...ctrl.paginatorOptions, 
				totalItems: ctrl.table.data.length
			};
		},
		reset: () => reset(),
		filterBy: null,
		range: ctrl.loginRanges[0]
	};
	ctrl.paginatorOptions = {};

	function onInit() {
		ctrl.form = appState.forms.selected;
		ctrl.permissions = appState.permissions.Administration.User;

		refresh();

		const thisUser = $userProfile.getUser();
		ctrl.isSysAdmin = thisUser.roles.indexOf(ROLE_TYPES.sysAdmin) > -1;

		// Watch for search filter changes to sync URL
		$scope.$watch(() => ctrl.table.filterBy, (newValue, oldValue) => {
			if(newValue !== oldValue) {
				$location.search("search", newValue || null).replace();
				ctrl.table.filter();
			}
		});

		// Watch for state param changes to handle deep linking
		$scope.$on("$locationChangeSuccess", () => {
			const searchParams = $location.search();
			handleStateChange({ 
				search: searchParams.search, 
				id: searchParams.id, 
				action: searchParams.action 
			});
		});
	}

	async function refresh() {
		await getUsers();
		
		// Handle initial deep link after data is loaded
		handleStateChange({ 
			search: $stateParams.search, 
			id: $stateParams.id, 
			action: $stateParams.action 
		});
	}

	/**
	 * Centralized handler for state changes - evaluates search and modal state
	 */
	function handleStateChange({
		search, 
		id, 
		action 
	} = {}) {
		// Handle search change - sync filter
		if(search !== ctrl.table.filterBy) {
			ctrl.table.filterBy = search || null;
		}

		// Handle modal state (id/action)
		handleModalState({ id, action });
	}

	/**
	 * Handle modal state based on URL params - opens user or add modal
	 */
	let currentModalId = null;

	function handleModalState({ id, action } = {}) {
		// Skip if a modal is already open — URL changes from child modals or tab
		// navigation within the modal should not trigger a new top-level modal
		if(currentModalId !== null) {
			return;
		}
		// action and id are mutually exclusive - id takes precedence
		if(id) {
			if($location.search().action) {
				$location.search("action", null).replace();
			}
			const user = ctrl.table.allData.find(u => u.Id == id);
			if(user) {
				openModal(user);
			}
			else {
				resetModal();
			}
		}
		else if(action === "add") {
			openModal(null);
		}
	}

	async function openModal(user) {
		currentModalId = $location.search().id || "new";
		try {
			await $uibModal.open({
				component: "user-modal",
				size: "xlg",
				resolve: {
					user: () => user,
					syncUrl: () => true
				}
			}).result;
			
			await getUsers();
			ctrl.table.filter();
		}
		finally {
			currentModalId = null;
			resetModal();
		}
	}

	function resetModal() {
		$location
			.search("id", null)
			.search("action", null)
			.hash("")
			.replace();
	}

	function windowResizeDesktopCallback() {
	}

	function reset() {
		ctrl.table.filterBy = null;
		ctrl.table.filter();
	}

	function resetPassword(user) {
		const params = {
			Username: user.Username
		};

		const promise = api.post(PASSWORD_RESET, params).then(() => {
			toastr.success(
				`A password reset email has been sent to ${user.Username}.`
			);
		});

		ctrl.promiseTracker.addPromise(promise);
	}

	async function resendInvite(user) {
		try {
			await api.post(INVITE_RESEND, { User: user });
			toastr.success(`An invitation email was sent to ${user.Username}.`);
		}
		catch(err) {
			$logger.exception("invitation email failed to send");
		}
	}

	function getUsers() {
		const route = FORM_USERS(ctrl.form.Id);
		const promise = api.get(route).then(response => {
			const createName = filters.fullNameFilter();
			const users = response.map(user => ({
				...user,
				Name: createName(user.Contact),
				meta: { username: `${user?.IsRequestOutstanding ? "0" : "1"}${user?.Username?.toLowerCase()}` } 
			}));
			ctrl.table.data = users;
			ctrl.table.allData = users;
			ctrl.searcher.setCollection(users);
			ctrl.paginatorOptions = {
				...ctrl.paginatorOptions, 
				totalItems: ctrl.table.data.length
			};
		});

		ctrl.promiseTracker.addPromise(promise);
		return promise;
	}

	function onCollapse() {
		ctrl.tableWidthCss = ctrl.isCollapsed ? "col-sm-12" : "col-sm-9";
	}

	function impersonate() {
		$logger.debug("impersonate not-implemented");
		toastr.error("This feature is no longer implemented");
	}

	function onLastLoginChange() {
		ctrl.table.filter();
	}
}

export default angular
	.module("vesl.screens.users", [])
	.component("users", { template, controller }).name;
